import * as React from 'react';
import { useState, useEffect } from 'react';
import { Button, Typography, Box } from '@mui/material';
import Table from '../Table';
import { useColumnDefinition } from './column-definitions';
import { MUIDataTableOptions } from 'mui-datatables';
import CustomDialog from '../Dialogs';
import { useAtcomResTasks } from '../../hooks/use-atcomres-tasks';
import * as TFF from '@tff/types/tff';
import { IntlMessages } from '../../util';
import SearchForm from './SearchForm';
import { useInvoluntaryChangesAtcom } from '../../hooks/use-involuntary-changes-atcom';

const InvoluntaryChangesAtCom = () => {
  const [retrieveItems, setRetrieveItems] = useState<TFF.InvoluntaryTask[]>([]);
  const [openProgressDialog, setOpenProgressDialog] = useState<boolean>(false);
  const [openCloseTasksDialog, setOpenCloseTasksDialog] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [retrievesToChange, setRetrievesToChange] = useState<TFF.InvoluntaryTask[]>([]);

  const { changeStateOfSelected } = useAtcomResTasks();
  const { involuntaryChangesResponse, setInvoluntaryChangesRequest, searchDone } = useInvoluntaryChangesAtcom();
  const columnDefinition = useColumnDefinition();

  useEffect(() => {
    if (involuntaryChangesResponse) {
      setRetrieveItems(involuntaryChangesResponse);
    }
  }, [involuntaryChangesResponse]);

  useEffect(() => {
    if (selectedRows) {
      const retrievesToChange: TFF.InvoluntaryTask[] = [];
      selectedRows.forEach(row => retrievesToChange.push(retrieveItems[row]));
      setRetrievesToChange(retrievesToChange);
    }
  }, [selectedRows]);

  type CustomToolbarProps = {
    selectedRows: number[];
  };

  const handleInProgressToolbar = (selectedRows: number[]) => {
    setSelectedRows(selectedRows);
    setOpenProgressDialog(true);
  };

  const handleCloseTasks = (selectedRows: number[]) => {
    setSelectedRows(selectedRows);
    setOpenCloseTasksDialog(true);
  };

  const handleSearch = (product: string, archive: boolean, recordLocator: string) => {
    setInvoluntaryChangesRequest({ product, archive, recordLocator });
  };

  const CustomToolbar: React.FC<CustomToolbarProps> = ({ selectedRows }) => {
    return (
      <div style={{ display: 'flex', gap: '4px', justifySelf: 'end', marginInlineEnd: '4px' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => handleInProgressToolbar(selectedRows)}
          disabled={!selectedRows.length}
        >
          <IntlMessages id="pages.involuntary-changes.set-in-progress" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => handleCloseTasks(selectedRows)}
          disabled={!selectedRows.length}
        >
          <IntlMessages id="pages.involuntary-changes.close" />
        </Button>
      </div>
    );
  };

  const tableOptions: MUIDataTableOptions = {
    selectableRows: 'multiple',
    selectableRowsHeader: true,
    customToolbar: () => {
      return <CustomToolbar selectedRows={[]} />;
    },
    customToolbarSelect: selectedRows => {
      return <CustomToolbar selectedRows={selectedRows.data.map(d => d.index)} />;
    },
  };

  const renderContent = () => {
    if (!searchDone) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" mt={4}>
          <Typography variant="h6">Please select a product and start search</Typography>
        </Box>
      );
    }

    if (!retrieveItems || retrieveItems.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" mt={4}>
          <Typography variant="h6">Sorry, no matching results found</Typography>
        </Box>
      );
    }

    return (
      <div style={{ marginTop: '50px' }}>
        <Table
          data={retrieveItems}
          columnsData={columnDefinition}
          tableId="involuntaryChangesAtCom"
          tableName={'Involuntary Changes AtCom'}
          otherOptions={tableOptions}
        />
      </div>
    );
  };

  return (
    <div className="tff-search-page-root">
      <SearchForm handleSearch={handleSearch} />
      {renderContent()}
      {openProgressDialog && (
        <CustomDialog
          initialOpen={openProgressDialog}
          onCancel={() => setOpenProgressDialog(false)}
          onConfirm={() => {
            changeStateOfSelected('OPEN', 'IN_PROGRESS', retrievesToChange);
            setOpenProgressDialog(false);
          }}
          width="md"
        >
          <Typography color="primary" variant="h6">
            Change the state of selected bookings to <strong>in progress</strong>
          </Typography>
        </CustomDialog>
      )}
      {openCloseTasksDialog && (
        <CustomDialog
          initialOpen={openCloseTasksDialog}
          onCancel={() => setOpenCloseTasksDialog(false)}
          onConfirm={() => {
            changeStateOfSelected('CONFIRMED', 'CLOSE', retrievesToChange);
            setOpenCloseTasksDialog(false);
          }}
          width="md"
        >
          <Typography color="primary" variant="h6">
            <IntlMessages id="pages.involuntary-changes.change-state-of-booking" />
            <strong>
              <IntlMessages id="pages.involuntary-changes.close" />
            </strong>
          </Typography>
        </CustomDialog>
      )}
    </div>
  );
};

export default InvoluntaryChangesAtCom;
